import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { X as CloseIcon, Eye as ViewIcon, Calendar as CalendarIcon, Search as SearchIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "../../../helpers/Endpoints/endpoints";
import { MecordColors } from "Constants/Color";
import MasterTableContainer from "../../TagsSearchPage/MasterFiltersTable/MasterTableContainer";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { SalesOrderViewComponent } from "./SalesOrderViewComponent";
import { mergeDashboardFilters, DashboardColFilter, scopedDropdownProps } from "./dashboardSidebarFilters";

interface DashboardSalesOrderSidebarProps {
  open: boolean;
  onClose: () => void;
  type: "product" | "service";
  dateRange: {
    from_date: string;
    to_date: string;
  } | null;
  kpiId?: string;
}

export const DashboardSalesOrderSidebar: React.FC<DashboardSalesOrderSidebarProps> = ({
  open,
  onClose,
  type,
  dateRange,
  kpiId = "orders_received",
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<DashboardColFilter[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const isJobsPending = kpiId === "jobs_pending_completion";
  const isPipelineOrderOwn = kpiId === "pipeline_order_own";

  const fetchOrders = async () => {
    if (!dateRange) return;

    setLoading(true);
    try {
      if (isJobsPending) {
        const params = new URLSearchParams({
          page: String(page + 1),
          page_size: String(rowsPerPage),
        });
        if (search) params.set("search", search);
        if (sortField) {
          params.set(
            "ordering",
            sortDirection === "desc" ? `-${sortField}` : sortField
          );
        }

        const filters = mergeDashboardFilters({}, filterValue);
        const response = await Axios.post(
          `/dashboard/service/individual/jobs-pending-completion?${params.toString()}`,
          { filters },
        );
        setData(response.data?.data || []);
        setTotalCount(response.data?.totalRecords || 0);
        return;
      }

      if (isPipelineOrderOwn) {
        const params = new URLSearchParams({
          from_date: dateRange.from_date,
          to_date: dateRange.to_date,
          period: "custom",
          page: String(page + 1),
          page_size: String(rowsPerPage),
        });
        if (search) params.set("search", search);
        if (sortField) {
          params.set(
            "ordering",
            sortDirection === "desc" ? `-${sortField}` : sortField
          );
        }

        const endpoint =
          type === "product"
            ? "/dashboard/sales/individual/pipeline-order-own"
            : "/dashboard/service/individual/pipeline-order-own";

        const filters = mergeDashboardFilters({}, filterValue);
        const response = await Axios.post(
          `${endpoint}?${params.toString()}`,
          { filters },
        );
        setData(response.data?.data || []);
        setTotalCount(response.data?.totalRecords || 0);
        return;
      }

      const url =
        type === "product"
          ? API_ENDPOINT.SALES_ORDER.PRODUCT.LIST
          : API_ENDPOINT.SALES_ORDER.SERVICE.LIST;

      const dateField = type === "product" ? "sales_order_date" : "srv_date";
      const isPipeline = kpiId.startsWith("pipeline_");
      const baseKpi = kpiId.replace("pipeline_", "");

      const isDispatchedKpi = baseKpi === "orders_dispatched";
      const isDispatchedPipeline = baseKpi === "dispatched" || (isPipeline && baseKpi === "orders_dispatched");
      const isDispatchedFilter = isDispatchedKpi || isDispatchedPipeline;

      const isPackedFilter = baseKpi === "orders_packed";
      const isJobCompletedFilter = baseKpi === "job_completed";

      const isOrderReceivedKpi = baseKpi === "orders_received" || baseKpi === "service_order_received";
      const isOrderReceivedPipeline = baseKpi === "order_received" || baseKpi === "order_own" || (isPipeline && (baseKpi === "orders_received" || baseKpi === "service_order_received"));
      const isOrderReceivedFilter = isOrderReceivedKpi || isOrderReceivedPipeline;

      // Job Completed KPI counts by sales_order_status + service_completion_date
      // (not sales_order_stage / srv_date).
      const baseFilters: any = isPackedFilter
        ? {
            packing_status: "Packed",
            packing_date_from: dateRange.from_date,
            packing_date_to: dateRange.to_date,
          }
        : isDispatchedFilter
        ? {
            dispatch_status: "Dispatched",
            ...(isDispatchedPipeline ? { quotation_product_id: { not: null } } : {}),
          }
        : isJobCompletedFilter
        ? {
            sales_order_status: "Full Service Completed",
            sales_order_stage: { not: "draft" },
            range: {
              service_completion_date: {
                from: dateRange.from_date,
                to: dateRange.to_date,
              },
            },
          }
        : {
            range: {
              [dateField]: {
                from: dateRange.from_date,
                to: dateRange.to_date,
              },
            },
          };

      if (isOrderReceivedFilter) {
        baseFilters.sales_order_stage = { not: "draft" };
        if (isOrderReceivedPipeline) {
          if (type === "product") {
            baseFilters.quotation_product = {
              is_deleted: false,
              quotation_stage: "log",
              quotation_status: "PO Received",
              enquiry_product_id: { not: null },
            };
          } else {
            baseFilters.quotation_service = {
              is_deleted: false,
              quotation_stage: "log",
              quotation_status: "PO Received",
              enquiry_service_id: { not: null },
            };
          }
        }
      }

      const filters = mergeDashboardFilters(baseFilters, filterValue);

      // Keep KPI locks
      if (isPackedFilter) filters.packing_status = "Packed";
      if (isDispatchedFilter) {
        filters.dispatch_status = "Dispatched";
        if (isDispatchedPipeline) {
          filters.quotation_product_id = { not: null };
        }
      }
      if (isJobCompletedFilter) {
        filters.sales_order_status = "Full Service Completed";
        filters.sales_order_stage = { not: "draft" };
      }
      if (isOrderReceivedFilter) {
        filters.sales_order_stage = { not: "draft" };
        if (isOrderReceivedPipeline) {
          if (type === "product") {
            filters.quotation_product_id = { not: null };
          } else {
            filters.quotation_service_id = { not: null };
          }
        }
      }

      const params = new URLSearchParams();
      params.append("page", String(page + 1));
      params.append("page_size", String(rowsPerPage));
      if (search) params.append("search", search);
      if (sortField) {
        params.append(
          "ordering",
          sortDirection === "desc" ? `-${sortField}` : sortField
        );
      }

      const response = await Axios.post(`${url}?${params.toString()}`, {
        filters,
      });

      setData(response.data?.data || []);
      setTotalCount(response.data?.totalRecords || 0);
    } catch (error) {
      console.error("Failed to fetch orders inside dashboard sidebar:", error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (open && dateRange) {
      fetchOrders();
    }
  }, [
    open,
    type,
    dateRange,
    page,
    rowsPerPage,
    kpiId,
    search,
    filterValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchInput("");
      setSearch("");
      setSorting([]);
      setColumnFilters([]);
      setFilterValue([]);
    }
  }, [open, type, dateRange, kpiId]);

  useEffect(() => {
    if (open) setPage(0);
  }, [filterValue, sortField, sortDirection]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || "").toLowerCase();
    if (s.includes("complete") || s.includes("dispatch") || s.includes("packed")) return "success";
    if (s.includes("lost") || s.includes("cancel")) return "error";
    if (s.includes("progress") || s.includes("pending")) return "warning";
    return "default";
  };

  const themeColor = MecordColors?.ThemeBGColor || "#d2363a";
  const headerBgColor = MecordColors?.LightThemeBGColor || "#f8e8e8";

  const kpiBaseFilters = useMemo(() => {
    if (!dateRange) return {};
    if (isJobsPending || isPipelineOrderOwn) return {};

    const dateField = type === "product" ? "sales_order_date" : "srv_date";
    const isDispatchedFilter = kpiId === "orders_dispatched";
    const isPackedFilter = kpiId === "orders_packed";
    const isJobCompletedFilter = kpiId === "job_completed";

    const base: any = isPackedFilter
      ? {
          packing_status: "Packed",
          packing_date_from: dateRange.from_date,
          packing_date_to: dateRange.to_date,
        }
      : isDispatchedFilter
        ? { dispatch_status: "Dispatched" }
        : isJobCompletedFilter
          ? {
              sales_order_status: "Service Completed",
              sales_order_stage: { not: "draft" },
              range: {
                service_completion_date: {
                  from: dateRange.from_date,
                  to: dateRange.to_date,
                },
              },
            }
          : {
              range: {
                [dateField]: {
                  from: dateRange.from_date,
                  to: dateRange.to_date,
                },
              },
            };

    if (kpiId === "orders_received" || kpiId === "service_order_received") {
      base.sales_order_stage = { not: "draft" };
    }
    return base;
  }, [dateRange, type, kpiId, isJobsPending, isPipelineOrderOwn]);

  const dateExtra = useMemo(
    () =>
      dateRange
        ? {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
            period: "custom",
          }
        : undefined,
    [dateRange],
  );

  const columns = useMemo(() => {
    const dateField = type === "product" ? "sales_order_date" : "srv_date";
    const source = isJobsPending
      ? "jobs_pending"
      : isPipelineOrderOwn
        ? "pipeline_order_own"
        : type === "product"
          ? "sales_order_product"
          : "sales_order_service";
    const dropdownExtra = isPipelineOrderOwn ? dateExtra : undefined;

    return [
      {
        header: "Order No.",
        accessorKey: "sales_order_number",
        enableSorting: true,
        ...scopedDropdownProps(source, "sales_order_number", kpiBaseFilters, dropdownExtra),
        cell: ({ row }: { row: any }) => (
          <span style={{ fontWeight: 600, color: themeColor }}>
            {row.original.sales_order_number ||
              row.original.sales_order_product_number ||
              "-"}
          </span>
        ),
      },
      {
        header: "Date",
        accessorKey: dateField,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) =>
          formatDate(row.original.sales_order_date || row.original.srv_date),
      },
      {
        header: "Customer Name",
        accessorKey: "company_name",
        enableSorting: true,
        ...scopedDropdownProps(source, "company_name", kpiBaseFilters, dropdownExtra),
        cell: ({ row }: { row: any }) =>
          row.original.customer_name ||
          row.original.company_name ||
          row.original.customer_details?.company_name ||
          "-",
      },
      {
        header: "Status",
        accessorKey: "sales_order_status",
        enableSorting: true,
        ...scopedDropdownProps(source, "sales_order_status", kpiBaseFilters, dropdownExtra),
        cell: ({ row }: { row: any }) => {
          const statusValue =
            row.original.sales_order_status || row.original.sales_order_stage;
          return (
            <Chip
              label={statusValue || "N/A"}
              color={getStatusColor(statusValue)}
              size="small"
              sx={{
                fontSize: "10px",
                height: 20,
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                borderRadius: "4px",
              }}
            />
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const orderId =
            type === "product"
              ? row.original.sales_order_product_id
              : row.original.sales_order_service_id;
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon size={12} />}
              onClick={() => {
                setViewingOrderId(orderId);
              }}
              sx={{
                fontSize: "11px",
                py: 0.25,
                px: 1.25,
                fontFamily: "'Poppins', sans-serif",
                textTransform: "none",
                borderColor: themeColor,
                color: themeColor,
                borderRadius: "6px",
                fontWeight: 500,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: themeColor,
                  backgroundColor: headerBgColor,
                  transform: "scale(1.02)",
                },
              }}
            >
              View
            </Button>
          );
        },
      },
    ];
  }, [type, themeColor, headerBgColor, kpiBaseFilters, isJobsPending, isPipelineOrderOwn, dateExtra]);

  const getTitle = () => {
    if (kpiId === "orders_packed") return "Orders Packed";
    if (kpiId === "orders_dispatched") return "Orders Dispatched";
    if (kpiId === "job_completed") return "Jobs Completed";
    if (kpiId === "jobs_pending_completion") return "Jobs Pending Completion";
    if (kpiId === "pipeline_order_own") return "Order Own";
    return "Orders Received";
  };

  return (
    <>
      <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          "@media (min-width: 768px)": {
            width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
          },
          p: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "-16px 0 48px rgba(0, 0, 0, 0.16), -4px 0 12px rgba(0, 0, 0, 0.08), inset 1px 0 0 rgba(255, 255, 255, 0.8)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, pb: 1.5, boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: "1px solid #e5e7eb",
            mb: 2.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  color: "#111827",
                  fontSize: "18px",
                }}
              >
                {getTitle()}
              </Typography>
              <Chip
                label={type === "product" ? "Sales" : "Service"}
                size="small"
                sx={{
                  backgroundColor: headerBgColor,
                  color: themeColor,
                  fontWeight: 600,
                  fontSize: "11px",
                  height: 20,
                  borderRadius: "6px",
                  fontFamily: "'Poppins', sans-serif"
                }}
              />
            </Box>
            {dateRange && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6b7280" }}>
                <CalendarIcon size={12} />
                <Typography sx={{ fontSize: "11px", fontFamily: "'Poppins', sans-serif" }}>
                  Period: {formatDate(dateRange.from_date)} to {formatDate(dateRange.to_date)}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        {/* Search bar */}
        <Box sx={{ mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by order number, customer name, agent..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={18} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    sx={{ color: "#9ca3af" }}
                  >
                    <CloseIcon size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                "& fieldset": {
                  borderColor: "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#d1d5db",
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColor,
                },
              },
            }}
          />
        </Box>

        {/* Listing Table Area */}
        <Box
          className="sidebar-table-scroll-container"
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.02)",
            position: "relative"
          }}
        >
          <MasterTableContainer
            columns={columns}
            data={data}
            totalRecords={totalCount}
            pageIndex={page}
            pageSize={rowsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setRowsPerPage}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
          />
         </Box>
      </Box>
    </Drawer>

      {/* 2. Nested Drawer: View Single Sales Order Details Inline */}
      <Drawer
        anchor="right"
        open={!!viewingOrderId}
        onClose={() => setViewingOrderId(null)}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
        PaperProps={{
          sx: {
            width: "100%",
            "@media (min-width: 768px)": {
              width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
            },
            p: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
            borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "-24px 0 54px rgba(0, 0, 0, 0.22), -6px 0 16px rgba(0, 0, 0, 0.1)",
            zIndex: 1400, // Positioned layered on top of the first list drawer
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, pb: 1.5, boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" }}>
          {/* Details Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 2,
              borderBottom: "1px solid #e5e7eb",
              mb: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  color: "#111827",
                  fontSize: "18px",
                }}
              >
                Sales Order Details
              </Typography>
            </Box>
            <IconButton onClick={() => setViewingOrderId(null)} size="small" sx={{ color: "#9ca3af" }}>
              <CloseIcon size={20} />
            </IconButton>
          </Box>

          {/* Details View Body */}
          <Box
            className="view-drawer-content"
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              pr: 0.5,
              position: "relative",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: "3px" },
              /* Condensed child card styling styles override */
              "& .card": {
                boxShadow: "none !important",
                border: "none !important",
                mb: "0px !important",
                backgroundColor: "transparent !important",
              },
              "& .card-body": {
                p: "0px !important",
              },
              "& hr": {
                my: "12px !important",
                borderColor: "#e5e7eb !important",
              },
              "& .section": {
                mb: "12px !important",
              },
              "& p, & td, & th": {
                fontSize: "13px !important",
                lineHeight: "1.4 !important",
              },
              "& p": {
                mb: "6px !important",
              },
              "& .table th, & .table td": {
                p: "8px 10px !important",
              }
            }}
          >
            {viewingOrderId ? (
              <SalesOrderViewComponent id={viewingOrderId} type={type} />
            ) : null}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
